import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PagesRoutes } from "@app/router";
import { ApiError } from "@shared/api";
import {
  useForgotPasswordApi,
  useResetPasswordApi,
  useValidateResetPasswordCodeApi,
} from "@services/auth";
import { Button } from "@shared/components";
import { useTranslation } from "@shared/locales";
import { validateConfirm, validateOtp, validatePassword } from "../utils";
import type { FieldErrors } from "../utils";
import { AuthShell } from "./AuthShell";
import { FormField } from "./FormField";

type ResetField = "otp" | "password" | "confirm";

/**
 * Steps 2 and 3 of the reset flow.
 *
 * Split into two panes because the backend exposes a dedicated
 * `validate-reset-password-code` endpoint: checking the code first means a
 * mistyped OTP is caught before the user has invented a new password. The
 * email is carried in the query string from step 1 purely for the copy and the
 * resend link — `POST reset-password` identifies the user by the code alone.
 */
export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";

  const [codeConfirmed, setCodeConfirmed] = useState(false);
  const [otp, setOtp] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<FieldErrors<ResetField>>({});
  const [otpServerError, setOtpServerError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const showCodeError = (error: unknown, fallbackKey: string) => {
    const fieldMessage = ApiError.isApiError(error)
      ? error.fieldError("reset_password_code")
      : undefined;

    if (fieldMessage) {
      setOtpServerError(fieldMessage);
      return;
    }
    setServerError((error as Error | null)?.message ?? t(fallbackKey));
  };

  const validateCode = useValidateResetPasswordCodeApi({
    withoutToastMessage: true,
    navigateOnError: false,
    onSuccess: () => {
      setServerError(null);
      setOtpServerError(null);
      setCodeConfirmed(true);
    },
    onError: (error) => showCodeError(error, "auth.errors.otpInvalidOrExpired"),
  });

  const resetPassword = useResetPasswordApi({
    withoutToastMessage: true,
    navigateOnError: false,
    onSuccess: () => navigate(PagesRoutes.auth.login),
    onError: (error) => {
      showCodeError(error, "auth.errors.otpInvalidOrExpired");
      if (
        ApiError.isApiError(error) &&
        error.fieldError("reset_password_code")
      ) {
        setCodeConfirmed(false);
      }
    },
  });

  // Resending surfaces its outcome in the same banner as the main submit —
  // `withoutToastMessage` suppresses success and error toasts together, so
  // there is no "success toast, banner error" split to reach for here.
  const resendCode = useForgotPasswordApi({
    withoutToastMessage: true,
    navigateOnError: false,
    onSuccess: () => setServerError(null),
    onError: (error) =>
      setServerError(error?.message ?? t("auth.errors.accountNotFound")),
  });

  const handleCodeSubmit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);
    setOtpServerError(null);

    const otpError = validateOtp(otp);
    setErrors({ otp: otpError });
    if (otpError) return;

    validateCode.mutate({ reset_password_code: otp.trim() });
  };

  const handlePasswordSubmit = (event: FormEvent) => {
    event.preventDefault();
    setServerError(null);
    setOtpServerError(null);

    const nextErrors: FieldErrors<ResetField> = {
      password: validatePassword(password),
      confirm: validateConfirm(password, confirm),
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    resetPassword.mutate({
      reset_password_code: otp.trim(),
      password,
      password_confirmation: confirm,
    });
  };

  return (
    <AuthShell
      title={t("auth.reset.title")}
      description={t("auth.reset.description", {
        email: email || t("auth.reset.yourEmail"),
      })}
      serverError={serverError}
      footer={
        email ? (
          <>
            {t("auth.reset.noCode")}{" "}
            <button
              type="button"
              onClick={() => resendCode.mutate({ email })}
              disabled={resendCode.isPending}
              className="font-medium text-foreground hover:underline disabled:opacity-50"
            >
              {t("auth.reset.resend")}
            </button>
          </>
        ) : (
          <Link
            to={PagesRoutes.auth["forgot-password"]}
            className="font-medium text-foreground hover:underline"
          >
            {t("auth.reset.requestNewCode")}
          </Link>
        )
      }
    >
      {codeConfirmed ? (
        <form
          onSubmit={handlePasswordSubmit}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormField
            id="reset-password"
            type="password"
            label={t("auth.fields.newPassword")}
            placeholder="••••••••"
            autoComplete="new-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            hint={t("auth.fields.passwordHint")}
            error={errors.password && t(errors.password)}
          />
          <FormField
            id="reset-confirm"
            type="password"
            label={t("auth.fields.confirmNewPassword")}
            placeholder="••••••••"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            error={errors.confirm && t(errors.confirm)}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={resetPassword.isPending}
          >
            {resetPassword.isPending
              ? t("auth.reset.submitting")
              : t("auth.reset.submit")}
          </Button>
        </form>
      ) : (
        <form
          onSubmit={handleCodeSubmit}
          className="flex flex-col gap-4"
          noValidate
        >
          <FormField
            id="reset-otp"
            inputMode="numeric"
            label={t("auth.fields.otp")}
            placeholder="123456"
            autoComplete="one-time-code"
            className="tracking-[0.4em]"
            value={otp}
            onChange={(event) => {
              setOtp(event.target.value);
              setOtpServerError(null);
            }}
            error={errors.otp ? t(errors.otp) : (otpServerError ?? undefined)}
          />
          <Button
            type="submit"
            className="w-full"
            disabled={validateCode.isPending}
          >
            {validateCode.isPending
              ? t("auth.reset.verifying")
              : t("auth.reset.verifyCode")}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
